import React, { FunctionComponent } from 'react';
import { range } from 'lodash';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { Select } from '../../Input/Select/Select';
import { Toggle } from '../../Input/Toggle/Toggle';
import { Radio } from '../../Input/Radio/Radio';
import { TextField } from '../../Input/TextField/TextField';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DAY_LIST, PICKUP_OR_DELIVERY } from '../../../utils/constants';
import { addDefaultPickupDelivery } from '../defaults';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { wholeNumberValidator } from '../../../utils/validation';
import styles from '../styles.module.scss';

const HOURS_OPTIONS = [
  ...[12, ...range(1, 12)].map((i) => `${i}:00 AM`),
  ...[12, ...range(1, 12)].map((i) => `${i}:00 PM`),
].map((label, index) => ({ label, value: index }));

const STARTS_AFTER_OPTIONS = range(1, 25).map((i) => ({
  label: `${i} Hour${i !== 1 ? 's' : ''}`,
  value: `${i} hours`,
}));

const ENDS_AFTER_OPTIONS = range(1, 25).map((i) => ({
  label: `${i} Day${i !== 1 ? 's' : ''}`,
  value: `${i} days`,
}));

const APPOINTMENT_WINDOW_OPTIONS = [
  { label: '15 Minutes', value: '15' },
  { label: '30 Minutes', value: '30' },
  { label: '60 Minutes', value: '60' },
];

export const DealerLaunchOnlinePickupDelivery: FunctionComponent<DealerLaunchStepProps<{
  type: 'pickup' | 'delivery';
}>> = ({
  dealer,
  step: {
    stepProps: { type },
  },
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({
    defaultValues: addDefaultPickupDelivery(dealer),
  });
  const enableDelivery = form.watch('wizard.delivery.enabled');
  const enablePickup = form.watch('wizard.pickup.enabled');
  const dayMap = DAY_LIST.reduce((acc: any, day: string) => {
    // eslint-disable-next-line no-param-reassign
    acc[day] = form.watch(`wizard.${type}.days.${day}.enabled`);
    return acc;
  }, {});

  const onSubmit = async (data: Dealer) => {
    // validate start time must be be before end time
    let hasTimeError = false;

    DAY_LIST.forEach((day) => {
      if (
        ((type === PICKUP_OR_DELIVERY.PICKUP && data.wizard?.pickup?.enabled) ||
          (type === PICKUP_OR_DELIVERY.DELIVERY &&
            data.wizard?.delivery?.enabled)) &&
        parseInt(`${data.wizard?.[type]?.days?.[day].start || 0}`, 10) >=
          parseInt(`${data.wizard?.[type]?.days?.[day].end || 0}`, 10) &&
        data.wizard?.[type]?.days?.[day].enabled
      ) {
        form.setError(`wizard.${type}.days.${day}.start`, {
          message: 'Start time must be before end time',
        });
        hasTimeError = true;
      }
    });

    if (hasTimeError) {
      return;
    }

    const dealerUpdate: Dealer = {
      ...data,
      wizard: {
        ...dealer.wizard,
        ...data.wizard,
      },
    };

    // force delivery radius into a number if it's a string
    if (
      type === PICKUP_OR_DELIVERY.DELIVERY &&
      typeof dealerUpdate.wizard?.delivery?.radius === 'string'
    ) {
      dealerUpdate.wizard.delivery.radius = parseInt(
        dealerUpdate.wizard.delivery.radius || '0',
        10,
      );
    }

    // force start and end times into number
    DAY_LIST.forEach((day) => {
      const dayObject = dealerUpdate.wizard?.[type]?.days;
      if (dayObject) {
        dayObject[day].start = parseInt(`${dayObject[day].start}`, 10);
        dayObject[day].end = parseInt(`${dayObject[day].end}`, 10);
      }
    });

    await nextStep(dealerUpdate);
  };

  const renderTimeInput = () => (
    <>
      <div className={styles.stepColumn}>
        {DAY_LIST.map((day) => (
          <div className={styles.timeInputRow} key={day}>
            <Toggle
              className={styles.switchContainer}
              label={day}
              name={`wizard.${type}.days.${day}.enabled` as const}
              variant="default"
              form={form}
            />
            <span className={styles.openClosedContainer}>
              {dayMap[day] ? 'Open' : 'Closed'}
            </span>
            {dayMap[day] && (
              <div className={styles.stepRow}>
                <Select
                  label=""
                  aria-label={`${day} opening time`}
                  name={`wizard.${type}.days.${day}.start` as const}
                  width="sm"
                  options={HOURS_OPTIONS}
                  form={form}
                  validation={{ required: true }}
                />
                <div className={styles.hoursSeparator} />
                <Select
                  label=""
                  aria-label={`${day} closing time`}
                  name={`wizard.${type}.days.${day}.end` as const}
                  width="sm"
                  options={HOURS_OPTIONS}
                  form={form}
                  validation={{ required: true }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      {type === PICKUP_OR_DELIVERY.DELIVERY && (
        <TextField
          label="Set delivery distance (miles)"
          tooltip="How far your dealership will deliver the vehicle for free."
          name="wizard.delivery.radius"
          width="sm"
          form={form}
          validation={{ required: true, pattern: wholeNumberValidator }}
        />
      )}
      <div className={styles.stepRow}>
        <Select
          label={
            type === PICKUP_OR_DELIVERY.PICKUP
              ? 'When is the earliest that a customer can pick up after purchase?'
              : 'When is the earliest that a vehicle can be delivered after purchase?'
          }
          tooltip={
            type === PICKUP_OR_DELIVERY.PICKUP
              ? 'IE: Vehicle pick up begins 12 hours after customer confirms online.'
              : 'IE: Delivery can be arranged 12 hours after customer confirms online.'
          }
          name={`wizard.${type}.startsAfter` as const}
          width="lg"
          options={STARTS_AFTER_OPTIONS}
          form={form}
          validation={{ required: true }}
        />
        <Select
          label={
            type === PICKUP_OR_DELIVERY.PICKUP
              ? 'How long after purchase can a customer pick up?'
              : 'How long after purchase can a vehicle be delivered?'
          }
          tooltip={
            type === PICKUP_OR_DELIVERY.PICKUP
              ? 'IE: Vehicle pick up ends 10 days after customer confirms online.'
              : 'IE: Delivery can be arranged up to 5 days after customer confirms online.'
          }
          name={`wizard.${type}.endsAfter` as const}
          width="md"
          options={ENDS_AFTER_OPTIONS}
          form={form}
          validation={{ required: true }}
        />
      </div>

      {type === PICKUP_OR_DELIVERY.PICKUP && (
        <Select
          label="How long are your appointment windows?"
          name="wizard.pickup.appointmentWindow"
          width="md"
          options={APPOINTMENT_WINDOW_OPTIONS}
          form={form}
          validation={{ required: true }}
        />
      )}
    </>
  );

  return (
    <DealerLaunchFormCardLayout
      className={styles.pickupDeliveryStep}
      form={form}
      title={
        type === PICKUP_OR_DELIVERY.PICKUP
          ? 'How Would You Like To Handle Pickup?'
          : 'Tell Us about your delivery options'
      }
      cardTitle={
        type === PICKUP_OR_DELIVERY.PICKUP
          ? 'Do you offer pickup?'
          : 'Do you offer delivery?'
      }
      cardSubTitle={
        type === PICKUP_OR_DELIVERY.PICKUP
          ? "Set the hours that you're open for customers to pickup their vehicles."
          : ''
      }
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      {type === PICKUP_OR_DELIVERY.DELIVERY && (
        <>
          <Radio
            className="flex-column"
            name="wizard.delivery.enabled"
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            form={form}
          />
          <br />
        </>
      )}
      {type === PICKUP_OR_DELIVERY.PICKUP && (
        <>
          <Radio
            className="flex-column"
            name="wizard.pickup.enabled"
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            form={form}
          />
          <br />
        </>
      )}
      {((type === PICKUP_OR_DELIVERY.DELIVERY && enableDelivery) ||
        (type === PICKUP_OR_DELIVERY.PICKUP && enablePickup)) &&
        renderTimeInput()}
    </DealerLaunchFormCardLayout>
  );
};
